// Deployments (routes/deployments.py, signed in only): workflows as APIs.
// A deployment has a name, a permanent id (`dep_…`), a canonical address
// built from both (https://…/api/deployments/<name>-<tag>, the server's
// `docs.address`; never built here), for one made before ids its
// name-only address (`docs.old_address`), endpoints, keys and a call log; its
// page is its docs. The operator's side;
// Julia has her own routes. Every refusal is `{detail: {code, message}}`
// (workflowError reads it): 403 deployments_not_enabled (the flag is off:
// the pages send the operator to Published) and workflows_not_enabled (a
// start, a key or Deploy again with Workflows off); 404 not_found, no_key;
// 409 key_revoked, name_taken, frozen, already_undeployed, not_undeployed;
// 410 undeployed; 422 bad_name, bad_end, too_many_keys, wrong_name.
//
//   GET    …/deployments[?undeployed=false]           DeploymentList
//   GET    …/deployments/{name}                       DeploymentView (docs, keys, 20 newest calls)
//   GET    …/deployments/{name}/openapi.json          the door's OpenAPI document (Download)
//   POST   …/deployments/{name}/try                   TryIn → TryOut (refusals inside it)
//   GET    …/deployments/{name}/try-file?…            a Try answer's file (`open_url`, a session route)
//   GET    …/deployments/{name}/keys                  DeploymentKeyList
//   POST   …/deployments/{name}/keys                  {name, ends_on?} → 201 KeyMade (the secret, once)
//   POST   …/deployments/{name}/keys/{id}/revoke      DeploymentKey
//   PATCH  …/deployments/{name}/keys/{id}             {ends_on} → DeploymentKey
//   GET    …/deployments/{name}/calls[?before=]       DeploymentCallList (50 a page)
//   GET    …/deployments/{name}/activity[?days=14]    DeploymentActivity (calls counted: today, 7 days, per day and endpoint)
//   GET    …/deployments/{name}/versions              VersionList
//   POST   …/deployments/{name}/versions/{v}/deploy   ProposalView ("Go back to this…")
//   POST   …/deployments/{name}/undeploy              {} → the plan; {confirm} → done
//   POST   …/deployments/{name}/deploy-again          DeployAgainAnswer
//
// The flag and the P6 words come from GET …/workflows/capacity
// (`deployments`, `deploy_words`; absent = no). Not in the iPhone app's
// contract.

import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";

import { ApiError, apiFetch } from "@/lib/api";
import type { components } from "@/types/api.gen";

import { workflowErrorCode } from "./workflows";
import type { WorkflowCapacity } from "./workerSlots";

type S = components["schemas"];

export type DeploymentRow = S["DeploymentRow"];
export type DeploymentView = S["DeploymentView"];
export type DeploymentRecord = S["Deployment"];
export type DeploymentRead = S["DeploymentRead"];
export type DeploymentDocs = S["DeploymentDocs"];
export type DocsEndpoint = S["DocsEndpoint"];
export type DeploymentKey = S["DeploymentKey"];
export type DeploymentCall = S["DeploymentCall"];
export type DeploymentActivity = S["DeploymentActivity"];
export type KeyMade = S["KeyMade"];
export type TryIn = S["TryIn"];
export type TryOut = S["TryOut"];
export type TryFile = S["TryFile"];
export type UndeployPlan = S["UndeployPlan"];
export type UndeployAnswer = S["UndeployAnswer"];
export type VersionRow = S["VersionRow"];
export type DeployAgainAnswer = S["DeployAgainAnswer"];
export type DeploymentRun = S["Run"];
export type RunProgress = S["RunProgress"];
export type StartAnswer = S["StartAnswer"];
export type InputSpec = S["PublishedParameter"];
export type GoBackProposal = S["ProposalView"];

// ─── the flag (capacity) ─────────────────────────────────────────────────

/** The workspace has Deployments (`deployments`); undefined while unknown or on an api without the field. */
export function deploymentsFlag(capacity: WorkflowCapacity | undefined): boolean | undefined {
  return capacity?.deployments;
}

/** Deploy from words is in the image and Deployments are on (`deploy_words`, P6): Deploy, Change… and Go back show. False while unknown. */
export function deployWordsOn(capacity: WorkflowCapacity | undefined): boolean {
  return capacity?.deployments === true && capacity?.deploy_words === true;
}

/** The flag is off for this workspace: the app routes answer 403 `deployments_not_enabled`. */
export const flagOff = (error: unknown) => error instanceof ApiError && error.status === 403 && workflowErrorCode(error) === "deployments_not_enabled";

// ─── queries ─────────────────────────────────────────────────────────────

const enc = encodeURIComponent;
const root = (c: string) => `/api/customers/${enc(c)}/deployments`;
const one = (c: string, name: string) => `${root(c)}/${enc(name)}`;

export const deploymentKeys = {
  all: (c: string | null) => ["deployments", c] as const,
  list: (c: string | null) => ["deployments", c, "list"] as const,
  one: (c: string | null, name: string | null) => ["deployments", c, "one", name] as const,
  calls: (c: string | null, name: string | null) => ["deployments", c, "calls", name] as const,
  activity: (c: string | null, name: string | null, days: number) => ["deployments", c, "activity", name, days] as const,
  versions: (c: string | null, name: string | null) => ["deployments", c, "versions", name] as const,
  progress: (c: string | null, name: string | null, endpoint: string, runId: string | null) => ["deployments", c, "progress", name, endpoint, runId] as const,
};

const retry = (count: number, error: Error) => !(error instanceof ApiError && [403, 404, 410].includes(error.status)) && count < 1;

/** Everything a deployment shows: its list row, its page, its calls and versions. */
export function invalidateDeployment(qc: QueryClient, customerId: string, name?: string) {
  void qc.invalidateQueries({ queryKey: deploymentKeys.list(customerId) });
  if (name) {
    void qc.invalidateQueries({ queryKey: deploymentKeys.one(customerId, name) });
    void qc.invalidateQueries({ queryKey: deploymentKeys.calls(customerId, name) });
    void qc.invalidateQueries({ queryKey: ["deployments", customerId, "activity", name] });
    void qc.invalidateQueries({ queryKey: deploymentKeys.versions(customerId, name) });
  }
}

/** Every deployment of the workspace, undeployed ones included (the list hides them unless asked). Loading it migrates legacy keys. */
export function useDeployments(customerId: string | null) {
  return useQuery({
    queryKey: deploymentKeys.list(customerId),
    enabled: !!customerId,
    queryFn: async ({ signal }) => (await apiFetch<S["DeploymentList"]>(root(customerId!), { signal })).deployments,
    staleTime: 10_000,
    refetchOnWindowFocus: true,
    retry,
  });
}

/** One deployment: record, docs, keys, 20 newest calls. `pollMs` (from what was last read) refetches while a new key waits for its first call. */
export function useDeployment(customerId: string | null, name: string | null, { pollMs }: { pollMs?: (view: DeploymentView | undefined) => number | false } = {}) {
  return useQuery({
    queryKey: deploymentKeys.one(customerId, name),
    enabled: !!customerId && !!name,
    queryFn: ({ signal }) => apiFetch<DeploymentView>(one(customerId!, name!), { signal }),
    staleTime: 5_000,
    refetchOnWindowFocus: true,
    refetchInterval: (query) => (pollMs ? pollMs(query.state.data) : false),
    retry,
  });
}

/** The live endpoints that read a step of this workflow (the editor's Delete step says so first). Empty where Deployments are off. */
export function useDeploymentReads(customerId: string | null, slug: string | null) {
  return useQuery({
    queryKey: ["deployments", customerId, "reads", slug],
    enabled: !!customerId && !!slug,
    queryFn: ({ signal }) => apiFetch<DeploymentRead[]>(`/api/customers/${encodeURIComponent(customerId!)}/workflows/${encodeURIComponent(slug!)}/deployment-reads`, { signal }),
    staleTime: 30_000,
    retry: false,
  });
}

/** The calls counted (the Overview's strip, the Activity tab, each endpoint's last good answer): today, 7 days, `days` by day. */
export function useDeploymentActivity(
  customerId: string | null,
  name: string | null,
  { days = 14, enabled = true, pollMs = false }: { days?: number; enabled?: boolean; pollMs?: number | false } = {},
) {
  return useQuery({
    queryKey: deploymentKeys.activity(customerId, name, days),
    enabled: enabled && !!customerId && !!name,
    queryFn: ({ signal }) => apiFetch<DeploymentActivity>(`${one(customerId!, name!)}/activity?days=${days}`, { signal }),
    staleTime: 15_000,
    refetchOnWindowFocus: true,
    refetchInterval: pollMs,
    retry,
  });
}

/** Every call, newest first, 50 a page (`before` pages on). */
export function useDeploymentCalls(customerId: string | null, name: string | null, { enabled = true }: { enabled?: boolean } = {}) {
  return useInfiniteQuery({
    queryKey: deploymentKeys.calls(customerId, name),
    enabled: enabled && !!customerId && !!name,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) =>
      apiFetch<S["DeploymentCallList"]>(`${one(customerId!, name!)}/calls${pageParam ? `?before=${enc(pageParam)}` : ""}`, { signal }),
    getNextPageParam: (last) => last.next_before ?? null,
    staleTime: 0,
    retry,
  });
}

/** Every version, newest first. */
export function useDeploymentVersions(customerId: string | null, name: string | null, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: deploymentKeys.versions(customerId, name),
    enabled: enabled && !!customerId && !!name,
    queryFn: async ({ signal }) => (await apiFetch<S["VersionList"]>(`${one(customerId!, name!)}/versions`, { signal })).versions,
    staleTime: 10_000,
    retry,
  });
}

/** The deployment's OpenAPI document, as the door answers it (the Download button saves it). */
export function fetchOpenapi(customerId: string, name: string) {
  return apiFetch<Record<string, unknown>>(`${one(customerId, name)}/openapi.json`);
}

/** A started Try run's progress (the start endpoint's `runs/{id}`), every 15 s while `live`. */
export function useTryProgress(customerId: string | null, name: string | null, endpoint: string, runId: string | null, { live }: { live: boolean }) {
  return useQuery({
    queryKey: deploymentKeys.progress(customerId, name, endpoint, runId),
    enabled: !!customerId && !!name && !!runId,
    queryFn: () => apiFetch<TryOut>(`${one(customerId!, name!)}/try`, { method: "POST", body: { endpoint, run_id: runId } }),
    refetchInterval: live ? 15_000 : false,
    staleTime: 0,
    retry: false,
  });
}

// ─── changes ─────────────────────────────────────────────────────────────

/** Try it: the door's own answer for a Try caller (a refusal comes back inside TryOut, not as an error). */
export function useTryEndpoint() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, name, body }: { customerId: string; name: string; body: TryIn }) =>
      apiFetch<TryOut>(`${one(customerId, name)}/try`, { method: "POST", body }),
    // its call line shows under Activity and in the counts
    onSettled: (_r, _e, v) => {
      void qc.invalidateQueries({ queryKey: deploymentKeys.one(v.customerId, v.name) });
      void qc.invalidateQueries({ queryKey: ["deployments", v.customerId, "activity", v.name] });
      void qc.invalidateQueries({ queryKey: deploymentKeys.calls(v.customerId, v.name) });
    },
  });
}

/** A new key; its secret comes back this once and nowhere else. */
export function useMakeKey() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, name, keyName, endsOn, can }: { customerId: string; name: string; keyName: string; endsOn: string | null; can?: "read" | "all" }) =>
      apiFetch<KeyMade>(`${one(customerId, name)}/keys`, { method: "POST", body: { name: keyName, ends_on: endsOn, ...(can ? { can } : {}) } }),
    onSettled: (_r, _e, v) => invalidateDeployment(qc, v.customerId, v.name),
  });
}

/** Revoke a key (again is fine; allowed with Workflows off). */
export function useRevokeKey() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, name, keyId }: { customerId: string; name: string; keyId: string }) =>
      apiFetch<DeploymentKey>(`${one(customerId, name)}/keys/${enc(keyId)}/revoke`, { method: "POST" }),
    onSettled: (_r, _e, v) => invalidateDeployment(qc, v.customerId, v.name),
  });
}

/** When a key ends: a London day it last works, null = never. */
export function useChangeKeyEnd() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, name, keyId, endsOn }: { customerId: string; name: string; keyId: string; endsOn: string | null }) =>
      apiFetch<DeploymentKey>(`${one(customerId, name)}/keys/${enc(keyId)}`, { method: "PATCH", body: { ends_on: endsOn } }),
    onSettled: (_r, _e, v) => invalidateDeployment(qc, v.customerId, v.name),
  });
}

/** Undeploy: without `confirm` the plan only (nothing done); with it, done (the name when the plan says `type_name`). */
export function useUndeploy() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, name, confirm }: { customerId: string; name: string; confirm?: string }) =>
      apiFetch<UndeployAnswer>(`${one(customerId, name)}/undeploy`, { method: "POST", body: confirm === undefined ? {} : { confirm } }),
    onSettled: (r, _e, v) => {
      if (r?.done !== false) invalidateDeployment(qc, v.customerId, v.name);
    },
  });
}

/** Deploy again: the same record back to live; within 30 days its keys too. */
export function useDeployAgain() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, name }: { customerId: string; name: string }) => apiFetch<DeployAgainAnswer>(`${one(customerId, name)}/deploy-again`, { method: "POST" }),
    onSettled: (_r, _e, v) => invalidateDeployment(qc, v.customerId, v.name),
  });
}

/** "Go back to this…": a proposal with that version's endpoints, opened on the Deploy screen. */
export function useGoBack() {
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, name, v }: { customerId: string; name: string; v: number }) =>
      apiFetch<GoBackProposal>(`${one(customerId, name)}/versions/${v}/deploy`, { method: "POST" }),
  });
}
